<template>
  <div class="role-list-wrapper">
    <!-- Toast Notifications -->
    <CToaster :autohide="3000" position="top-right">
      <CToast
        v-for="(info, index) in infoList"
        :key="`toast-${index}`"
        :show="true"
        :header="info.header"
        :color="info.color"
      >
        {{ info.message }}
      </CToast>
    </CToaster>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-user-shield me-2"></i>
                  <strong>Role Management</strong>
                </h5>
                <small class="text-muted">Manage system roles and permissions</small>
              </div>
              <div class="header-actions">
                <CBadge color="info" class="result-counter">
                  {{ items.length }} {{ items.length === 1 ? 'Role' : 'Roles' }}
                </CBadge>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Loading State -->
            <div v-if="loading && items.length === 0" class="loading-state">
              <div class="text-center py-5">
                <CSpinner color="primary" />
                <p class="text-muted mt-3">Loading roles...</p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && items.length === 0" class="empty-state">
              <div class="text-center py-5">
                <i class="fas fa-user-shield fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No roles found</h5>
                <p class="text-muted">Get started by creating your first role</p>
                <CButton color="primary" @click="addNew" class="mt-2">
                  <i class="fas fa-plus me-2"></i>
                  Add New Role
                </CButton>
              </div>
            </div>

            <!-- Data View (Mobile + Desktop) -->
            <div v-else>
            <!-- Mobile Card View (visible on small screens) -->
            <div class="mobile-view d-md-none">
              <div v-for="(item, index) in items" :key="item.id" class="mobile-card">
                <div class="mobile-card-header" @click="onEdit(item)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="role-number text-muted">
                          <small>#{{ index + 1 }}</small>
                        </div>
                        <CBadge :color="item.isSystemRole ? 'warning' : 'secondary'" size="sm">
                          {{ item.isSystemRole ? 'System' : 'Custom' }}
                        </CBadge>
                      </div>
                      <h6 class="role-name mb-1">
                        <i class="fas fa-user-shield me-2 text-primary"></i>
                        {{ item.name }}
                      </h6>
                      <div class="text-muted mb-1" v-if="item.displayName">
                        <small><strong>Display:</strong> {{ item.displayName }}</small>
                      </div>
                      <div class="text-muted" v-if="item.description">
                        <small>{{ item.description }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mobile-card-footer">
                  <div class="d-flex gap-2">
                    <CButton
                      size="sm"
                      color="primary"
                      variant="outline"
                      @click="onEdit(item)"
                      style="flex: 1"
                    >
                      <i class="fas fa-edit me-1"></i>
                      Edit
                    </CButton>
                    <CButton
                      size="sm"
                      color="danger"
                      variant="outline"
                      @click.stop="confirmDelete(item)"
                      :disabled="item.isSystemRole"
                      title="Delete Role"
                      style="flex: 1"
                    >
                      <i class="fas fa-trash me-1"></i>
                      Delete
                    </CButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop Table View (hidden on small screens) -->
            <div class="desktop-view d-none d-md-block">
              <div class="table-responsive">
                <CDataTable
                  :items="items"
                  :fields="fields"
                  :hover="true"
                  :loading="loading"
                  @row-clicked="onEdit"
                  class="professional-table"
                >
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">#{{ index + 1 }}</span>
                    </td>
                  </template>

                  <template #name="{ item }">
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="fas fa-user-shield text-primary me-2"></i>
                        <strong>{{ item.name }}</strong>
                      </div>
                    </td>
                  </template>

                  <template #displayName="{ item }">
                    <td>
                      <span>{{ item.displayName || '-' }}</span>
                    </td>
                  </template>

                  <template #description="{ item }">
                    <td>
                      <span class="text-muted">{{ item.description || 'No description' }}</span>
                    </td>
                  </template>

                  <template #isSystemRole="{ item }">
                    <td class="text-center">
                      <CBadge :color="item.isSystemRole ? 'warning' : 'secondary'">
                        {{ item.isSystemRole ? 'System' : 'Custom' }}
                      </CBadge>
                    </td>
                  </template>

                  <template #show_details="{ item }">
                    <td class="text-end">
                      <CButton
                        color="primary"
                        variant="outline"
                        size="sm"
                        @click="onEdit(item)"
                        class="me-2"
                      >
                        <i class="fas fa-edit me-1"></i>
                        Edit
                      </CButton>
                      <CButton
                        color="danger"
                        variant="outline"
                        size="sm"
                        @click.stop="confirmDelete(item)"
                        :disabled="item.isSystemRole"
                        title="Delete Role"
                      >
                        <i class="fas fa-trash me-1"></i>
                        Delete
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </div>
            </div>
            </div>
          </CCardBody>

          <!-- Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton color="primary" @click="addNew">
                  <i class="fas fa-plus me-2"></i>
                  Add New Role
                </CButton>
                <CButton
                  color="success"
                  variant="outline"
                  @click="exportCSV"
                  class="ms-2"
                >
                  <i class="fas fa-download me-2"></i>
                  Export CSV
                </CButton>
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Total: {{ items.length }} {{ items.length === 1 ? 'role' : 'roles' }}
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Delete Confirmation Modal -->
    <CModal
      :show.sync="deleteModal.show"
      color="danger"
      :centered="true"
    >
      <template #header>
        <h5 class="modal-title">
          <i class="fas fa-exclamation-triangle me-2"></i>
          Confirm Delete
        </h5>
      </template>
      <div v-if="deleteModal.item">
        <p class="mb-2">Are you sure you want to delete this role?</p>
        <div class="alert alert-warning mb-0">
          <strong>Role:</strong> {{ deleteModal.item.name }}<br>
          <strong>Display Name:</strong> {{ deleteModal.item.displayName || 'N/A' }}
          <div v-if="deleteModal.item.description" class="mt-2">
            <strong>Description:</strong> {{ deleteModal.item.description }}
          </div>
        </div>
        <p class="text-danger mt-3 mb-0">
          <i class="fas fa-exclamation-circle me-1"></i>
          <strong>Warning:</strong> This action cannot be undone.
        </p>
      </div>
      <template #footer>
        <CButton color="secondary" @click="deleteModal.show = false">
          <i class="fas fa-times me-1"></i>
          Cancel
        </CButton>
        <CButton color="danger" @click="deleteRole" :disabled="deleteModal.deleting">
          <CSpinner v-if="deleteModal.deleting" size="sm" class="me-1" />
          <i v-else class="fas fa-trash me-1"></i>
          {{ deleteModal.deleting ? 'Deleting...' : 'Delete Role' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import RoleApi from "../../lib/roleApi";

export default {
  name: "RoleList",
  data() {
    return {
      loading: true,
      items: [],
      infoList: [],
      deleteModal: {
        show: false,
        item: null,
        deleting: false,
      },
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "name",
          label: "Role Name",
          _style: "min-width:150px;"
        },
        {
          key: "displayName",
          label: "Display Name",
          _style: "min-width:150px;"
        },
        {
          key: "description",
          label: "Description",
          _style: "min-width:200px;"
        },
        {
          key: "isSystemRole",
          label: "System Role",
          _style: "width:120px;text-align:center;"
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:180px",
          sorter: false,
          filter: false,
        },
      ],
      api: new RoleApi(),
    };
  },
  mounted() {
    this.refreshTable();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    refreshTable() {
      this.loading = true;
      this.api
        .getList()
        .then((response) => {
          this.items = response.result || [];
          this.loading = false;
        })
        .catch((error) => {
          this.toast("Error", "Failed to load roles", "danger");
          this.loading = false;
        });
    },

    onEdit(item) {
      this.$router.push({
        path: `/admins/Role/${item.id}`,
      });
    },

    addNew() {
      this.$router.push({ path: "/admins/Role" });
    },

    confirmDelete(item) {
      if (item.isSystemRole) {
        this.toast("Warning", "System roles cannot be deleted", "warning");
        return;
      }

      this.deleteModal.item = item;
      this.deleteModal.show = true;
    },

    deleteRole() {
      if (!this.deleteModal.item) return;

      this.deleteModal.deleting = true;

      this.api
        .delete(this.deleteModal.item.id)
        .then(() => {
          this.toast("Success", `Role "${this.deleteModal.item.name}" deleted successfully`, "success");
          this.deleteModal.show = false;
          this.deleteModal.item = null;
          this.deleteModal.deleting = false;

          // Refresh the table to show updated list
          this.refreshTable();
        })
        .catch((error) => {
          this.toast(
            "Error",
            error.response?.data?.message || "Failed to delete role. It may be in use.",
            "danger"
          );
          this.deleteModal.deleting = false;
        });
    },

    exportCSV() {
      const csvData = this.items.map((item, index) => ({
        '#': index + 1,
        'Role Name': item.name,
        'Display Name': item.displayName || '',
        'Description': item.description || '',
        'System Role': item.isSystemRole ? 'Yes' : 'No',
      }));

      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `roles_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.toast("Success", "CSV exported successfully", "success");
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.role-list-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

.result-counter {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.5;
}

/* Mobile Card View */
.mobile-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: white;
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

.mobile-card-header {
  padding: 1rem 1.25rem;
  cursor: pointer;
  background: white;
}

.mobile-card-header:hover {
  background: var(--cui-body-bg, #f8f9fa);
}

.role-number {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.role-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--cui-body-color, #212529);
}

.mobile-card-footer {
  padding: 0.75rem 1.25rem;
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.professional-table tbody tr:hover {
  background-color: var(--cui-table-hover-bg, rgba(0, 0, 0, 0.075));
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .role-list-wrapper {
    padding: 1rem;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .card-footer {
    padding: 1rem;
  }

  .footer-actions {
    width: 100%;
  }

  .footer-actions .btn {
    flex: 1;
  }
}

/* Print Styles */
@media print {
  .card-footer,
  .mobile-card-footer,
  .show_details {
    display: none !important;
  }

  .mobile-view {
    display: none !important;
  }

  .desktop-view {
    display: block !important;
  }
}
</style>
